import React, { useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, RefreshCw, X, RotateCw, Package, AlertTriangle, CheckCircle2, Clock, Loader2, Link2 } from 'lucide-react';

const HEALTH = {
  ok: { cls: 'bg-green-100 text-green-700', Icon: CheckCircle2 },
  warning: { cls: 'bg-amber-100 text-amber-700', Icon: AlertTriangle },
  error: { cls: 'bg-red-100 text-red-700', Icon: AlertTriangle },
  pending: { cls: 'bg-slate-100 text-slate-600', Icon: Clock },
  unknown: { cls: 'bg-slate-100 text-slate-600', Icon: Clock },
};

const HealthBadge = ({ health }) => {
  const h = HEALTH[health?.code] || HEALTH.unknown;
  const Icon = h.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${h.cls}`} title={health?.label}>
      <Icon className="w-3 h-3" /> {health?.label || health?.code}
    </span>
  );
};

// Pickup (ritiro a domicilio) state as surfaced by the backend summary.
const PICKUP = {
  none:   { cls: 'bg-slate-100 text-slate-500', label: 'Non richiesto' },
  booked: { cls: 'bg-green-100 text-green-700', label: 'Prenotato' },
  todo:   { cls: 'bg-amber-100 text-amber-700', label: 'Da prenotare' },
  failed: { cls: 'bg-red-100 text-red-700',     label: 'Errore ritiro' },
};

const PickupBadge = ({ state }) => {
  const p = PICKUP[state] || PICKUP.none;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${p.cls}`}>{p.label}</span>;
};

const fmtDate = (v) => (v ? new Date(v).toLocaleString('it-IT') : '-');
// Date-only (yyyy-MM-dd) -> dd/MM/yyyy, timezone-safe (no Date parsing).
const fmtDay = (v) => { if (!v) return '-'; const [y, m, d] = String(v).split('-'); return d ? `${d}/${m}/${y}` : v; };

export const AdminShipments = ({ token, API }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [q, setQ] = useState('');
  const [onlyProblems, setOnlyProblems] = useState(true);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refinalizing, setRefinalizing] = useState('');
  const [linkForm, setLinkForm] = useState(null); // { ldv, courier, note } while the manual-LDV form is open
  const [linking, setLinking] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/shipments`, {
        headers,
        params: { q: q.trim(), only_problems: onlyProblems, limit: 50 },
      });
      setItems(res.data.items || []);
      if ((res.data.items || []).length === 0) toast.info('Nessuna spedizione trovata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ricerca non riuscita');
    } finally {
      setLoading(false);
    }
  }, [q, onlyProblems, API, token]);

  const openDetail = async (key) => {
    setLinkForm(null);
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const res = await axios.get(`${API}/admin/shipments/${encodeURIComponent(key)}`, { headers });
      setDetail(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dettaglio non disponibile');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refinalize = async (key) => {
    if (!window.confirm('Ri-finalizzare questa spedizione? Verrà (ri)creata l\'etichetta e inviata l\'email al cliente.')) return;
    setRefinalizing(key);
    try {
      const res = await axios.post(`${API}/admin/shipments/${encodeURIComponent(key)}/refinalize`, {}, { headers });
      if (res.data.success) toast.success(res.data.message || 'Etichetta rigenerata');
      else toast.warning(res.data.message || 'Rifinalizzazione senza etichetta: controlla il dettaglio');
      await search();
      if (detail?.summary?.session_id === key || detail?.summary?.woo_order_id == key) await openDetail(key);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rifinalizzazione non riuscita');
    } finally {
      setRefinalizing('');
    }
  };

  const linkLdv = async (key) => {
    const ldv = (linkForm?.ldv || '').trim();
    if (!ldv) { toast.error('Inserisci la LDV'); return; }
    if (!window.confirm(`Collegare la LDV ${ldv} a questo ordine? L'ordine risulterà spedito e la Ri-finalizzazione verrà bloccata.`)) return;
    setLinking(true);
    try {
      const res = await axios.post(`${API}/admin/shipments/${encodeURIComponent(key)}/link-ldv`,
        { ldv, courier: linkForm.courier || '', note: linkForm.note || '' }, { headers });
      toast.success(res.data.message || 'LDV collegata');
      setLinkForm(null);
      await search();
      await openDetail(key);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Collegamento LDV non riuscito');
    } finally {
      setLinking(false);
    }
  };

  return (
    <div>
      {/* Search bar */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                className="pl-9"
                placeholder="session_id (cs_...), n° ordine o email cliente"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && search()}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 select-none">
              <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
              Solo problemi
            </label>
            <Button onClick={search} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-2">Cerca</span>
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            "Solo problemi" mostra gli ordini pagati senza etichetta valida (LDV mancante o finalizzazione fallita).
          </p>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Stato</th>
                <th className="px-4 py-2 font-medium">Ordine</th>
                <th className="px-4 py-2 font-medium">Servizio</th>
                <th className="px-4 py-2 font-medium">Ritiro</th>
                <th className="px-4 py-2 font-medium">Cliente</th>
                <th className="px-4 py-2 font-medium">Importo</th>
                <th className="px-4 py-2 font-medium">Data</th>
                <th className="px-4 py-2 font-medium text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Nessun risultato. Cerca per session_id, ordine o email.
                </td></tr>
              )}
              {items.map((it) => {
                const key = it.session_id || String(it.woo_order_id || it.id);
                return (
                  <tr key={it.id || key} className="border-t hover:bg-slate-50">
                    <td className="px-4 py-2"><HealthBadge health={it.health} /></td>
                    <td className="px-4 py-2">
                      {it.woo_order_number ? `#${it.woo_order_number}` : <span className="text-slate-400">—</span>}
                      <div className="text-[10px] text-slate-400 font-mono truncate max-w-[140px]" title={it.session_id}>{it.session_id}</div>
                    </td>
                    <td className="px-4 py-2">{it.servizio || '-'}</td>
                    <td className="px-4 py-2"><PickupBadge state={it.pickup_state} /></td>
                    <td className="px-4 py-2">
                      <div>{it.mittente || '-'}</div>
                      <div className="text-xs text-slate-400">{it.mittente_email}</div>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{it.amount != null ? `€ ${Number(it.amount).toFixed(2)}` : '-'}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-slate-500">{fmtDate(it.created_at)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <Button variant="outline" size="sm" onClick={() => openDetail(key)}>Dettagli</Button>
                      {it.health?.code !== 'ok' && it.health?.code !== 'pending' && (
                        <Button variant="outline" size="sm" className="ml-2 text-blue-600"
                          onClick={() => refinalize(key)} disabled={refinalizing === key}>
                          {refinalizing === key ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                          <span className="ml-1">Ri-finalizza</span>
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Dettaglio spedizione</h3>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto space-y-4">
              {detailLoading || detail.loading ? (
                <div className="py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
              ) : (
                <>
                  <div className="flex items-center gap-3 flex-wrap">
                    <HealthBadge health={detail.summary?.health} />
                    {detail.summary?.woo_order_number && <span className="text-sm">Ordine <b>#{detail.summary.woo_order_number}</b></span>}
                    {detail.summary?.tracking && <span className="text-sm">Tracking <b>{detail.summary.tracking}</b></span>}
                    {detail.summary?.doc_label && <span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{detail.summary.doc_label}</span>}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Field label="session_id" value={detail.summary?.session_id} mono />
                    <Field label="Servizio" value={detail.summary?.servizio} />
                    <Field label="Mittente" value={`${detail.summary?.mittente || ''} ${detail.summary?.mittente_email ? '· ' + detail.summary.mittente_email : ''}`} />
                    <Field label="Destinatario" value={detail.summary?.destinatario} />
                    <Field label="Importo" value={detail.summary?.amount != null ? `€ ${Number(detail.summary.amount).toFixed(2)}` : '-'} />
                    <Field label="LDV" value={(detail.summary?.ldv_ids || []).join(', ') || '—'} />
                    <Field label="Etichetta" value={detail.summary?.has_label ? (detail.summary?.label_url ? 'URL disponibile' : 'PDF salvato') : 'Assente'} />
                  </div>

                  {/* Ritiro a domicilio: cosa ha chiesto il cliente + esito prenotazione */}
                  <div className="border rounded p-3 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-slate-600">Ritiro a domicilio</div>
                      <PickupBadge state={detail.summary?.pickup_state} />
                    </div>
                    {detail.summary?.pickup_requested ? (
                      <div className="text-sm space-y-0.5">
                        <div>Richiesto dal cliente per il <b>{fmtDay(detail.summary?.pickup_req_date)}</b></div>
                        {detail.summary?.pickup_note && <div className="text-slate-600">Note: {detail.summary.pickup_note}</div>}
                        {detail.summary?.pickup_state === 'booked' && (
                          <div className="text-green-700">Prenotato — codice <b>{detail.summary.pickup_code}</b>{detail.summary?.pickup_date ? ` (${fmtDay(detail.summary.pickup_date)})` : ''}</div>
                        )}
                        {detail.summary?.pickup_state === 'failed' && (
                          <div className="text-red-700">Prenotazione fallita — <b>va prenotato manualmente</b> con il corriere.</div>
                        )}
                        {detail.summary?.pickup_state === 'todo' && (
                          <div className="text-amber-700">Richiesto ma non ancora prenotato — <b>da prenotare</b>.</div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500">Il cliente non ha richiesto il ritiro a domicilio (consegna in punto / drop-off).</div>
                    )}
                  </div>

                  {detail.summary?.finalize_error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded text-sm">
                      <b>Errore finalizzazione:</b> {detail.summary.finalize_error}
                    </div>
                  )}
                  {detail.summary?.pickup_error && (
                    <div className="p-3 bg-amber-50 text-amber-700 rounded text-sm">
                      <b>Errore ritiro:</b> {detail.summary.pickup_error}
                    </div>
                  )}

                  {/* Event log */}
                  <div>
                    <div className="text-sm font-medium text-slate-600 mb-2">Log fulfillment</div>
                    <div className="space-y-1">
                      {(detail.events || []).length === 0 && <div className="text-xs text-slate-400">Nessun evento registrato per questa sessione.</div>}
                      {(detail.events || []).map((ev, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${ev.ok ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="text-slate-400 w-36 shrink-0">{fmtDate(ev.created_at)}</span>
                          <span className="font-medium w-20 shrink-0">{ev.stage}</span>
                          <span className="text-slate-600 break-all">{ev.detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {detail.finalize_response && (
                    <details>
                      <summary className="text-sm font-medium text-slate-600 cursor-pointer">Risposta corriere (finalize)</summary>
                      <pre className="mt-2 p-3 bg-slate-900 text-slate-100 rounded text-[11px] overflow-x-auto max-h-60">{
                        (() => { try { return JSON.stringify(JSON.parse(detail.finalize_response), null, 2); } catch { return detail.finalize_response; } })()
                      }</pre>
                    </details>
                  )}

                  {linkForm && (
                    <div className="border rounded p-3 space-y-2 bg-slate-50">
                      <div className="text-sm font-medium text-slate-600">Collega LDV manuale</div>
                      <p className="text-xs text-slate-500">
                        Per spedizioni già create a mano (es. dal pannello BSSI). L'ordine verrà segnato come spedito
                        e la Ri-finalizzazione sarà bloccata per non creare un doppione.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <Input placeholder="LDV (es. 281018J117553)" value={linkForm.ldv}
                          onChange={(e) => setLinkForm({ ...linkForm, ldv: e.target.value })} />
                        <Input placeholder="Corriere (es. SDA)" value={linkForm.courier}
                          onChange={(e) => setLinkForm({ ...linkForm, courier: e.target.value })} />
                        <Input placeholder="Nota (facoltativa)" value={linkForm.note}
                          onChange={(e) => setLinkForm({ ...linkForm, note: e.target.value })} />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setLinkForm(null)} disabled={linking}>Annulla</Button>
                        <Button className="text-white" onClick={() => linkLdv(detail.summary.session_id || String(detail.summary.woo_order_id))}
                          disabled={linking}>
                          {linking ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}
                          Collega
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2 border-t">
                    {detail.summary?.label_url && (
                      <a href={detail.summary.label_url} target="_blank" rel="noreferrer">
                        <Button variant="outline"><Package className="w-4 h-4 mr-1" /> Apri etichetta</Button>
                      </a>
                    )}
                    {detail.summary?.health?.code !== 'ok' && detail.summary?.woo_order_id && !detail.summary?.has_label && !linkForm && (
                      <Button variant="outline" onClick={() => setLinkForm({ ldv: '', courier: 'SDA', note: '' })}>
                        <Link2 className="w-4 h-4 mr-1" /> Collega LDV manuale
                      </Button>
                    )}
                    {detail.summary?.health?.code !== 'ok' && detail.summary?.session_id && (
                      <Button className="text-white" onClick={() => refinalize(detail.summary.session_id)}
                        disabled={refinalizing === detail.summary.session_id}>
                        {refinalizing === detail.summary.session_id
                          ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCw className="w-4 h-4 mr-1" />}
                        Ri-finalizza
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono }) => (
  <div>
    <div className="text-xs text-slate-400">{label}</div>
    <div className={`break-all ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</div>
  </div>
);

export default AdminShipments;
